import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { Server, ServerBillingToken, ServerCreate } from '../../types';
import { Plus, Upload, RefreshCw, Copy } from 'lucide-react';
import Modal from '../../components/Modal';
import ServerList from './ServerList';
import ServerForm from './ServerForm';
import UploadConfigModal from './UploadConfigModal';

const ServerManagement: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Removed unused selectedServer state to satisfy no-unused-vars
  const [showCredentials, setShowCredentials] = useState<{ [key: string]: boolean }>({});
  const [editingServer, setEditingServer] = useState<Server | null>(null);
  const [generatedBillingToken, setGeneratedBillingToken] = useState<ServerBillingToken | null>(null);
  const [generatingTokenServerId, setGeneratingTokenServerId] = useState<string | null>(null);
  const [copiedBillingToken, setCopiedBillingToken] = useState(false);

  // Form states
  const [formData, setFormData] = useState<ServerCreate>({
    name: '',
    host: ''
  });

  // Upload states
  const [uploadServerId, setUploadServerId] = useState<string>('');
  const [uploadFileType, setUploadFileType] = useState<'supervisor' | 'controller'>('supervisor');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllServers();
      setServers(response);
      setError(null);
    } catch (err) {
      setError('Failed to fetch servers');
      console.error('Error fetching servers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newServer = await apiService.createServer(formData);
      setServers([...servers, newServer]);
      setShowCreateModal(false);
      setFormData({ name: '', host: '' });
      await generateBillingToken(newServer);
    } catch (err) {
      setError('Failed to create server');
      console.error('Error creating server:', err);
    }
  };

  const handleUpdateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingServer) return;

    try {
      const existingServer = servers.find(server => server.id === editingServer.id);
      const previousHost = existingServer?.host;
      const updatedServer = await apiService.updateServer(editingServer.id, {
        name: editingServer.name,
        host: editingServer.host,
        api_key: editingServer.api_key,
        api_secret: editingServer.api_secret,
        is_active: editingServer.is_active
      });
      setServers(servers.map(s => s.id === updatedServer.id ? updatedServer : s));
      setEditingServer(null);
      if (previousHost && previousHost !== updatedServer.host) {
        await generateBillingToken(updatedServer);
      }
    } catch (err) {
      setError('Failed to update server');
      console.error('Error updating server:', err);
    }
  };

  const handleDeleteServer = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this server?')) return;

    try {
      await apiService.deleteServer(id);
      setServers(servers.filter(s => s.id !== id));
    } catch (err) {
      setError('Failed to delete server');
      console.error('Error deleting server:', err);
    }
  };

  const handleRegenerateCredentials = async (serverId: string) => {
    if (!window.confirm('Are you sure you want to regenerate API credentials? This also invalidates the current BILLING_BACKEND_BEARER_TOKEN.')) return;

    try {
      const updatedServer = await apiService.regenerateServerCredentials(serverId);
      setServers(servers.map(s => s.id === updatedServer.id ? updatedServer : s));
      // Update editing server if it's the same server
      if (editingServer && editingServer.id === serverId) {
        setEditingServer(updatedServer);
      }
      // Show the regenerated credentials
      setShowCredentials(prev => ({ ...prev, [serverId]: true }));
      setError(null);
      await generateBillingToken(updatedServer);
    } catch (err) {
      setError('Failed to regenerate credentials');
      console.error('Error regenerating credentials:', err);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log(`${type} copied to clipboard`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const generateBillingToken = async (server: Server) => {
    try {
      setGeneratingTokenServerId(server.id);
      setCopiedBillingToken(false);
      const tokenResponse = await apiService.generateBillingBackendToken(server.id);
      setGeneratedBillingToken(tokenResponse);
      setError(null);
    } catch (err) {
      setError(`Failed to generate billing bearer token for ${server.name}`);
      console.error('Error generating billing bearer token:', err);
    } finally {
      setGeneratingTokenServerId(null);
    }
  };

  const handleCopyBillingToken = async () => {
    if (!generatedBillingToken) return;
    try {
      await navigator.clipboard.writeText(generatedBillingToken.access_token);
      setCopiedBillingToken(true);
    } catch (err) {
      console.error('Failed to copy billing bearer token:', err);
    }
  };

  
  const validateJsonStructure = (jsonData: any, fileType: 'supervisor' | 'controller'): { valid: boolean; error?: string } => {
    try {
      // Check if it's an object
      if (typeof jsonData !== 'object' || jsonData === null) {
        return { valid: false, error: 'JSON must be an object' };
      }

      // Check for required user object
      if (!jsonData.user || typeof jsonData.user !== 'object') {
        return { valid: false, error: 'Missing required "user" object' };
      }

      // Validate user object has required fields
      const user = jsonData.user;
      if (!user.id || !user.username || !user.email) {
        return { valid: false, error: 'User object must have id, username, and email fields' };
      }

      if (fileType === 'supervisor') {
        // For supervisor.json, check for supervisor object
        if (!jsonData.supervisor || typeof jsonData.supervisor !== 'object') {
          return { valid: false, error: 'supervisor.json must contain a "supervisor" object' };
        }

        const supervisor = jsonData.supervisor;
        if (!supervisor.id) {
          return { valid: false, error: 'Supervisor object must have an id field' };
        }
      } else if (fileType === 'controller') {
        // For controller.json, check for controllers array
        if (!jsonData.controllers || !Array.isArray(jsonData.controllers)) {
          return { valid: false, error: 'controller.json must contain a "controllers" array' };
        }

        if (jsonData.controllers.length === 0) {
          return { valid: false, error: 'Controllers array cannot be empty' };
        }

        // Validate each controller has required fields
        for (let i = 0; i < jsonData.controllers.length; i++) {
          const controller = jsonData.controllers[i];
          if (!controller.controller_id || !controller.controller_name) {
            return { valid: false, error: `Controller at index ${i} must have controller_id and controller_name fields` };
          }
        }
      }

      return { valid: true };
    } catch (error) {
      return { valid: false, error: 'Invalid JSON structure' };
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !uploadServerId) return;

    try {
      setUploading(true);

      // Read and validate file content
      const fileContent = await uploadFile.text();
      let jsonData;

      try {
        jsonData = JSON.parse(fileContent);
      } catch (parseError) {
        setError('Invalid JSON file. Please ensure the file contains valid JSON.');
        return;
      }

      // Validate JSON structure
      const validation = validateJsonStructure(jsonData, uploadFileType);
      if (!validation.valid) {
        setError(`Invalid ${uploadFileType}.json structure: ${validation.error}`);
        return;
      }

      const response = await apiService.uploadFile(uploadServerId, uploadFile, uploadFileType);
      alert(`File uploaded successfully: ${response.message}`);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadServerId('');
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload file');
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
    }
  };

  const toggleCredentials = (serverId: string) => {
    setShowCredentials(prev => ({
      ...prev,
      [serverId]: !prev[serverId]
    }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Server Management</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload Config
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Server
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <ServerList 
        servers={servers}
        setEditingServer={setEditingServer}
        handleDeleteServer={handleDeleteServer}
        toggleCredentials={toggleCredentials}
        showCredentials={showCredentials}
        onRegenerateCredentials={handleRegenerateCredentials}
        onGenerateBillingToken={generateBillingToken}
        generatingTokenServerId={generatingTokenServerId}
      />

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Add New Server">
        <ServerForm
          formData={formData}
          setFormData={setFormData}
          handleCreateServer={handleCreateServer}
        />
      </Modal>

      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Upload Configuration File">
        <UploadConfigModal
          servers={servers}
          uploadServerId={uploadServerId}
          setUploadServerId={setUploadServerId}
          uploadFileType={uploadFileType}
          setUploadFileType={setUploadFileType}
          uploadFile={uploadFile}
          setUploadFile={setUploadFile}
          handleFileUpload={handleFileUpload}
          uploading={uploading}
        />
      </Modal>

      {/* Edit Server Modal */}
      {editingServer && (
        <Modal
          isOpen={!!editingServer}
          onClose={() => setEditingServer(null)}
          title="Edit Server"
        >
          <form onSubmit={handleUpdateServer} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Server Name
              </label>
              <input
                type="text"
                value={editingServer.name}
                onChange={(e) => setEditingServer({ ...editingServer, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Server host
              </label>
              <input
                type="text"
                value={editingServer.host}
                onChange={(e) => setEditingServer({ ...editingServer, host: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Changing the source URL requires a new `BILLING_BACKEND_BEARER_TOKEN`. The UI generates one after you save the change.
              </p>
            </div>
            
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingServer.is_active}
                  onChange={(e) => setEditingServer({ ...editingServer, is_active: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>

            {editingServer.api_key && editingServer.api_secret && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">API Credentials</h4>
                  <button
                    type="button"
                    onClick={() => handleRegenerateCredentials(editingServer.id)}
                    className="flex items-center gap-1 text-orange-600 hover:text-orange-800 text-sm"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-600">API Key:</label>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(editingServer.api_key!, 'API Key')}
                        className="text-gray-500 hover:text-gray-700"
                        title="Copy API Key"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={editingServer.api_key}
                      onChange={(e) => setEditingServer({ ...editingServer, api_key: e.target.value })}
                      className="w-full text-xs font-mono bg-gray-100 p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter API Key"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-600">API Secret:</label>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(editingServer.api_secret!, 'API Secret')}
                        className="text-gray-500 hover:text-gray-700"
                        title="Copy API Secret"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={editingServer.api_secret}
                      onChange={(e) => setEditingServer({ ...editingServer, api_secret: e.target.value })}
                      className="w-full text-xs font-mono bg-gray-100 p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter API Secret"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingServer(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Update Server
              </button>
            </div>
          </form>
        </Modal>
      )}

      {generatedBillingToken && (
        <Modal
          isOpen={!!generatedBillingToken}
          onClose={() => {
            setGeneratedBillingToken(null);
            setCopiedBillingToken(false);
          }}
          title="BILLING_BACKEND_BEARER_TOKEN"
        >
          <div className="space-y-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              This token is displayed only once. Copy it now and store it as `BILLING_BACKEND_BEARER_TOKEN` in the website backend env.
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Server</label>
                <div className="rounded-md bg-gray-100 px-3 py-2 text-sm">{generatedBillingToken.name}</div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Source URL</label>
                <div className="rounded-md bg-gray-100 px-3 py-2 text-sm font-mono break-all">{generatedBillingToken.source_url}</div>
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">Bearer token</label>
                <button
                  type="button"
                  onClick={handleCopyBillingToken}
                  className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900"
                >
                  <Copy className="h-4 w-4" />
                  {copiedBillingToken ? 'Copied' : 'Copy'}
                </button>
              </div>
              <textarea
                readOnly
                value={generatedBillingToken.access_token}
                rows={8}
                className="w-full rounded-md border border-gray-300 bg-gray-50 p-3 font-mono text-xs"
              />
            </div>

            {generatedBillingToken.expires_at && (
              <div className="text-xs text-gray-500">
                Expires: {new Date(generatedBillingToken.expires_at).toLocaleString()}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ServerManagement;
